import { useState, useEffect, useRef } from 'react'; // Added useRef
import { useLocalStorage } from './useLocalStorage';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db, getCoverImage } from '../firebase'; // Import getCoverImage

export const useProjectData = (projectId) => {
    const [project, setProject] = useLocalStorage(`project_${projectId}`, null);
    const [loading, setLoading] = useState(() => {
        // Don't show loading when cached project data is available from useLocalStorage
        return project === null;
    });
    const isFetchingCoversRef = useRef(false); // Ref to prevent concurrent fetches
    const [error, setError] = useState(null);
    const [labels, setLabels] = useLocalStorage(`project_labels_${projectId}`, []);
    const [background, setBackground] = useLocalStorage(`project_background_${projectId}`, null);
    const [projectMembers, setProjectMembers] = useState({});

    useEffect(() => {
        // Immediately set loading to false if we have cached data
        if (project !== null) {
            setLoading(false);
        }

        const unsubscribe = onSnapshot(
            doc(db, 'projects', projectId),
            (doc) => {
                if (doc.exists()) {
                    const projectData = { id: doc.id, ...doc.data() };
                    setProject(prevProject => {
                        if (prevProject?.updatedAt === projectData.updatedAt) {
                            return prevProject;
                        }
                        return projectData;
                    });
                    setLabels(projectData.labels || []);
                    setBackground(projectData.background || null);
                    setLoading(false);
                } else {
                    setError('Project not found');
                    setLoading(false);
                }
            },
            (err) => {
                setError('Error loading project');
                setLoading(false);
                console.error('Error loading project:', err);
            }
        );

        return () => unsubscribe();
    }, [projectId, project]);

    useEffect(() => {
        const fetchMemberDetails = async () => {
            if (!project?.members) {
                setProjectMembers({});
                return;
            }

            try {
                const memberPromises = Object.entries(project.members).map(async ([uid, memberData]) => {
                    const userDoc = await getDoc(doc(db, 'users', uid));
                    if (userDoc.exists()) {
                        return [uid, { ...memberData, ...userDoc.data() }];
                    }
                    return [uid, memberData];
                });

                const memberDetails = Object.fromEntries(await Promise.all(memberPromises));
                setProjectMembers(memberDetails);
            } catch (error) {
                console.error('Error fetching member details:', error);
                setProjectMembers({});
            }
        };

        fetchMemberDetails();
    }, [project?.members]);

    // Effect to fetch cover images for cards
    useEffect(() => {
        if (!project || !project.columns || isFetchingCoversRef.current) {
            return;
        }

        const fetchAllCoverImages = async () => {
            isFetchingCoversRef.current = true;
            // setLoading(true); // REMOVED: Main loading state should not be tied to background cover processing

            let projectDataChanged = false;
            const newColumns = JSON.parse(JSON.stringify(project.columns)); // Deep clone for modification
            const promises = [];

            for (const columnId in newColumns) {
                const column = newColumns[columnId];
                if (column.cards && column.cards.length > 0) {
                    for (let i = 0; i < column.cards.length; i++) {
                        const card = column.cards[i];
                        const originalCard = project.columns[columnId].cards[i]; // For comparison

                        if (card.coverImageId) {
                            // Card has a coverImageId, try to fetch it
                            const promise = getCoverImage(card.coverImageId)
                                .then(imageDoc => {
                                    const newBase64 = (imageDoc && imageDoc.base64Image) ? imageDoc.base64Image : null;
                                    if (card.coverImageBase64 !== newBase64) {
                                        card.coverImageBase64 = newBase64;
                                        projectDataChanged = true;
                                    }
                                    if (!newBase64 && imageDoc) { // ID existed but image doc was empty or lacked base64
                                      console.warn(`Cover image data for ID ${card.coverImageId} was incomplete.`);
                                    } else if (!newBase64 && !imageDoc){
                                      console.warn(`Cover image document for ID ${card.coverImageId} not found. Clearing ID and local base64.`);
                                      // If the image document was not found, clear both ID and base64
                                      card.coverImageId = null; // Clear the ID
                                      if(card.coverImageBase64){ // if there was a local base64, clear it
                                        card.coverImageBase64 = null;
                                      }
                                      projectDataChanged = true; // Ensure change is flagged
                                    }
                                })
                                .catch(err => {
                                    console.error(`Failed to fetch cover image ${card.coverImageId} for card ${card.id}:`, err);
                                    // If fetch fails (e.g., network error), just clear base64, keep ID for retry
                                    if (card.coverImageBase64 !== null) {
                                        card.coverImageBase64 = null;
                                        projectDataChanged = true;
                                    }
                                });
                            promises.push(promise);
                        } else {
                            // Card does not have a coverImageId, ensure coverImageBase64 is also null
                            if (card.coverImageBase64 != null) { // Use != to catch undefined too
                                card.coverImageBase64 = null;
                                projectDataChanged = true;
                            }
                        }
                    }
                }
            }

            try {
                await Promise.allSettled(promises);
            } catch (e) {
                console.error("Error during Promise.allSettled for cover images:", e);
            }


            if (projectDataChanged) {
                setProject(prevProject => {
                    // Compare newColumns with prevProject.columns before updating
                    // This is a shallow comparison of the columns object itself,
                    // but deep changes within cards (coverImageBase64) are handled by projectDataChanged.
                    // A more robust deep comparison might be needed if other async processes modify prevProject.
                    // For now, relying on projectDataChanged.
                    if (JSON.stringify(prevProject.columns) !== JSON.stringify(newColumns)) {
                         return { ...prevProject, columns: newColumns };
                    }
                    return prevProject; // No actual change needed in project state if only coverImageBase64 was identical
                });
            }

            // setLoading(false); // REMOVED: Main loading state should not be tied to background cover processing
            isFetchingCoversRef.current = false;
        };

        // Determine if processing is needed.
        // This effect runs whenever `project` changes. We need to see if any card's
        // cover image status (ID vs base64) is inconsistent.
        let processingNeeded = false;
        if (project && project.columns) {
            for (const columnId in project.columns) {
                const column = project.columns[columnId];
                if (column.cards && column.cards.length > 0) {
                    for (const card of column.cards) {
                        if (card.coverImageId && !card.coverImageBase64) {
                            // Has ID but no image data yet
                            processingNeeded = true;
                            break;
                        }
                        if (!card.coverImageId && card.coverImageBase64) {
                            // No ID but has image data (stale, needs clearing)
                            processingNeeded = true;
                            break;
                        }
                        // A more complex check could be to see if coverImageId has changed
                        // and coverImageBase64 is still from the old ID, but that requires
                        // storing the ID associated with the current base64, which we don't.
                        // The current logic will re-fetch if ID exists and base64 is missing,
                        // and clear base64 if ID is missing. This should suffice.
                    }
                }
                if (processingNeeded) break;
            }
        }


        if (processingNeeded || (project && !project.coverImagesProcessed)) {
            // project.coverImagesProcessed is a hypothetical flag to run once after initial load from local storage
            // A simpler way is just to run fetchAllCoverImages if project.columns exists.
            // The internal checks (isFetchingCoversRef, projectDataChanged) manage updates.
             fetchAllCoverImages();
        } else if (!isFetchingCoversRef.current && loading && project && project.columns) {
            // If it was loading, but no processing was needed, set loading to false.
            // This case might occur if `project` changed but not in a way that requires cover reprocessing.
            setLoading(false);
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project]); // Changed dependency to `project` to react to any project change, including coverImageId updates from Firestore.
                  // The isFetchingCoversRef and careful state updates inside should prevent infinite loops.


    return {
        project,
        setProject, // Exposing setProject, though direct use outside might be rare if hook manages its state well
        loading,
        error,
        labels,
        background,
        projectMembers
    };
};
